import { defineStore } from 'pinia'
import { flatten } from 'lodash'
import repositoryFactory from '@/factories/repository-factory'
import WeatherWarning2Repository from '@/repositories/weather-warning2-repository'
import WeatherWarning2 from '@/models/WeatherWarning2'
import { Area, ALERT_KIND_STATUSES } from '@/models/WeatherWarning'
import { resolveAlertLevel } from '@/models/weatherWarningCode'
import { useInitDataStore } from '@/stores/initDataStore'

const storeKey = 'weatherWarning'

// 警戒レベル(2:注意報 / 3:警報 / 4:危険警報 / 5:特別警報) -> init定義(layoutInfo.header.notification)のヘッダーアイコンステータス
// iws3.1 と同様に weather_warn1:注意報 / weather_warn2:警報 / weather_warn3:危険警報 / weather_warn4:特別警報 とする
// init定義に該当ステータスが無い場合（weather_warn4 未定義の環境など）は、定義済みの最大ステータスで表示する
const headerStatus = (alertLevel: number) => {
  const statusLevel = Math.max(alertLevel - 1, 1)
  const initDataStore = useInitDataStore()
  const notification = initDataStore.initData.layoutInfo.header.notification?.find(
    icon => icon.id === 'notification_jma'
  )
  const definedLevels = (notification?.items || [])
    .map(item => Number(item.status.match(/^weather_warn(\d+)$/)?.[1]))
    .filter(level => level > 0 && level <= statusLevel)

  return `weather_warn${definedLevels.length > 0 ? Math.max(...definedLevels) : statusLevel}`
}

type Store = {
  weatherWarningData: WeatherWarning2[]
}

export const useWeatherWaringStore = defineStore(storeKey, {
  state: () =>
    ({
      weatherWarningData: [] as WeatherWarning2[],
    } as Store),

  actions: {
    async fetch(areaList: Area[]) {
      const repository = (await repositoryFactory.create()).get('weatherWarning2') as WeatherWarning2Repository

      this.weatherWarningData = await Promise.all(areaList.map(area => repository.get(area.code.toString())))
    },
  },

  getters: {
    // 注意報警報画面と同様に、解除以外（発表中）の kind・河川情報の警戒レベルをコード辞書から判定し、最大値を返す
    maxAlertLevel: state => {
      const kindCodes = flatten(state.weatherWarningData.map(value => value.kinds))
        .filter(kind => kind.status !== ALERT_KIND_STATUSES.CANCEL)
        .map(kind => kind.code)
      const riverCodes = flatten(state.weatherWarningData.map(value => value.rivers)).map(river => river.kindCode)
      const levels = [...kindCodes, ...riverCodes].map(code => resolveAlertLevel(code))

      if (levels.length == 0) {
        return 0
      }

      const maxLevel = Math.max(...levels)
      return headerStatus(maxLevel)
    },
  },
})
